import { z } from 'zod';
import { REEL_TYPES } from '../kinds.js';
import { THOUGHT_SOURCES, THOUGHT_STATUSES } from './brain.js';
import { BLOCK_ROLES, CONTENT_FORMATS, MANNERS } from './content.js';
import { PROMPT_CODES, REASONING_EFFORTS } from './prompts.js';

/*
 * The admin section of the brain: prompt versions, the model check, costs and
 * the thoughts of participants (spec 7.4, 16).
 */

export const promptSummarySchema = z.object({
  id: z.number().int(),
  code: z.enum(PROMPT_CODES),
  language: z.string(),
  version: z.number().int(),
  model: z.string(),
  reasoningEffort: z.enum(REASONING_EFFORTS).nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type PromptSummary = z.infer<typeof promptSummarySchema>;

export const promptDtoSchema = promptSummarySchema.extend({ body: z.string() });
export type PromptDto = z.infer<typeof promptDtoSchema>;

export const promptCreateSchema = z.object({
  code: z.enum(PROMPT_CODES),
  language: z.literal('ru').default('ru'),
  body: z.string().max(20_000),
  model: z.string().trim().min(1).max(128),
  reasoningEffort: z.enum(REASONING_EFFORTS).nullable(),
  note: z.string().trim().max(500).optional(),
});
export type PromptCreate = z.infer<typeof promptCreateSchema>;

export const modelCheckInputSchema = z.object({
  code: z.enum(PROMPT_CODES),
  model: z.string().trim().min(1).max(128),
  reasoningEffort: z.enum(REASONING_EFFORTS).nullable(),
});

export const MODEL_CHECK_RESULTS = ['ok', 'not_found', 'no_structured_outputs', 'no_reasoning', 'unavailable'] as const;
export type ModelCheckResult = (typeof MODEL_CHECK_RESULTS)[number];

/** OpenRouter slug: vendor/model, optionally with a leading ~ (a "-latest" alias). */
export const MODEL_SLUG_RE = /^~?[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9._:-]*$/i;

export const costSummarySchema = z.object({
  days: z.number().int(),
  totalUsd: z.number(),
  calls: z.number().int(),
  byKind: z.array(z.object({ kind: z.string(), usd: z.number(), calls: z.number().int(), failed: z.number().int() })),
  byModel: z.array(z.object({ model: z.string(), usd: z.number(), calls: z.number().int() })),
  byDay: z.array(z.object({ day: z.string(), usd: z.number() })),
  thought: z.object({ count: z.number().int(), avgUsd: z.number() }),
  draft: z.object({ count: z.number().int(), avgUsd: z.number() }),
  evalUsd: z.number(),
  topUsers: z.array(
    z.object({
      userId: z.number().int(),
      telegramId: z.number().int(),
      username: z.string().nullable(),
      firstName: z.string().nullable(),
      usd: z.number(),
    }),
  ),
});
export type CostSummaryDto = z.infer<typeof costSummarySchema>;

export const adminThoughtSchema = z.object({
  id: z.number().int(),
  userId: z.number().int(),
  telegramId: z.number().int(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  source: z.enum(THOUGHT_SOURCES),
  status: z.enum(THOUGHT_STATUSES),
  createdAt: z.string(),
  transcript: z.string().nullable(),
});
export type AdminThought = z.infer<typeof adminThoughtSchema>;

// ---- the check set (spec 7.5) ------------------------------------------------------

/** The shape of IdeaDraft of the ideas step (apps/api llm-schemas.ts). */
export const ideaDraftSchema = z.object({
  angle: z.string(),
  hook: z.string(),
  summary: z.string(),
  format: z.enum(CONTENT_FORMATS),
  /** Absent in outputs saved before engine 0.6.0: scenes. */
  type: z.enum(REEL_TYPES).optional(),
  whyFormat: z.string(),
});
export type IdeaDraftDto = z.infer<typeof ideaDraftSchema>;

/** The shape of DraftText of the text step (apps/api llm-schemas.ts). */
export const draftTextSchema = z.object({
  blocks: z.array(z.object({ role: z.enum(BLOCK_ROLES), text: z.string(), title: z.string().optional() })),
  suggestions: z.array(z.string()),
  manner: z.enum(MANNERS).nullable(),
});
export type DraftTextDto = z.infer<typeof draftTextSchema>;

export const EVAL_CODES = ['ideas', 'text_reel', 'text_carousel', 'text_diagram', 'text_listicle'] as const;
export type EvalCode = (typeof EVAL_CODES)[number];

export const evalItemSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  language: z.string(),
  transcript: z.string(),
  idea: ideaDraftSchema.nullable(),
  sourceThoughtId: z.number().int().nullable(),
  createdAt: z.string(),
});
export type EvalItemDto = z.infer<typeof evalItemSchema>;

export const evalItemCreateSchema = z.union([
  z.object({ thoughtId: z.number().int().positive() }),
  z.object({ title: z.string().trim().min(1).max(120), transcript: z.string().trim().min(1).max(20_000) }),
]);

export const evalRunCreateSchema = z.object({
  code: z.enum(EVAL_CODES),
  aPromptId: z.number().int().positive(),
  bPromptId: z.number().int().positive(),
});

export const evalRunSummarySchema = z.object({
  id: z.number().int(),
  code: z.enum(EVAL_CODES),
  status: z.enum(['running', 'done', 'failed']),
  aPromptId: z.number().int(),
  bPromptId: z.number().int(),
  items: z.number().int(),
  picked: z.number().int(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type EvalRunSummary = z.infer<typeof evalRunSummarySchema>;

/** One side as the owner sees it: never the prompt or the model. */
export const evalOutputViewSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ideas'), ideas: z.array(ideaDraftSchema) }),
  z.object({ kind: z.literal('text'), text: draftTextSchema }),
  z.object({ kind: z.literal('error'), message: z.string() }),
  z.object({ kind: z.literal('pending') }),
]);
export type EvalOutputView = z.infer<typeof evalOutputViewSchema>;

export const EVAL_CHOICES = ['first', 'second', 'tie'] as const;

const sideResultSchema = z.object({
  promptId: z.number().int(),
  version: z.number().int(),
  model: z.string(),
  wins: z.number().int(),
  failures: z.number().int(),
  avgUsd: z.number().nullable(),
  avgMs: z.number().nullable(),
});
export type SideResult = z.infer<typeof sideResultSchema>;

export const evalResultSchema = z.object({
  a: sideResultSchema,
  b: sideResultSchema,
  ties: z.number().int(),
  picked: z.number().int(),
  total: z.number().int(),
});
export type EvalResult = z.infer<typeof evalResultSchema>;

export const evalRunDetailSchema = z.object({
  run: evalRunSummarySchema,
  items: z.array(
    z.object({
      itemId: z.number().int(),
      title: z.string(),
      transcript: z.string(),
      idea: ideaDraftSchema.nullable(),
      first: evalOutputViewSchema,
      second: evalOutputViewSchema,
      choice: z.enum(EVAL_CHOICES).nullable(),
    }),
  ),
  /** null until every item is picked, unless reveal=1. */
  result: evalResultSchema.nullable(),
});
export type EvalRunDetail = z.infer<typeof evalRunDetailSchema>;

export const evalPickSchema = z.object({ itemId: z.number().int().positive(), choice: z.enum(EVAL_CHOICES) });
